import { inject, observer } from "mobx-react";
import {
  MediaModel,
  ProductAttributeMappingsInput,
  ProductAttributeWithOption,
  ProductAttributeInputModel,
  ProductVariantEditableModel,
  ProductItemModel,
  InventorySettings,
} from "../../../../../shared/models";
import React, { FC, useRef } from "react";
import AttributeSection from "./AttributeSection";
import AttributeSectionEditable from "./AttributeSectionEditable";
// import VariantPriceSection from './VariantPriceSection';
import ItemAlowSection from "../ItemAlowSection";
import ItemInventorySection from "../ItemInventorySection";
import VariantPriceSectionEditable from "./VariantPriceSectionEditable";
import VariantAdvancedSectionEditable from "./VariantAdvancedSectionEditable";
import LibraryIcon from "../../../../../../../../images/common/library-grey-folder.svg";
import UploadIcon from "../../../../../../../../images/common/upload-grey-shape.svg";
// import { ImgWrapper } from './Variant.styles';
import DeleteIcon from "../../../../../../../../images/products/trash-white.svg";
import EditIcon from "../../../../../../../../images/products/edit-white.svg";
import {
  CustomLabel,
  FlexContainer,
  IconButton,
  SapphireBlueButton,
  ToastService,
  Video,
} from "@mindmemobile/mm-shared-global";
import {
  ItemsStore,
  MediaStore,
  RolesAndPermissionStore,
} from "../../../../../shared/store";
import { MediaTypes } from "../../../../../shared/enums";
import { AuthService, MediaService } from "../../../../../shared/services";
import {
  ItemFrame,
  ItemImage,
  MainOuterCard,
} from "../../../../../shared/styles";
import { MediaLibraryPopUp } from "../../../../../shared/components";

type IProps = {
  itemStore?: ItemsStore;
  rolesAndPermissionStore: RolesAndPermissionStore;
};

const VariantRightSection: FC<IProps> = ({
  itemStore,
  rolesAndPermissionStore,
}) => {
  const mediaStore = new MediaStore();
  const fileUploadBtnRef = useRef<HTMLInputElement>(null);

  const uploadClick = () => {
    if (fileUploadBtnRef.current) {
      fileUploadBtnRef.current.click();
    } else {
    }
  };
 
  if (!itemStore?.selectedProduct?.variants) return null;

  const variant =
    itemStore.selectedProduct.variants.options[
      itemStore?.selectedProduct.selectedVariantIndex
    ];

  const renderMedia = (media: MediaModel) => {
    switch (media.type) {
      case MediaTypes.Video:
        return <Video url={media.fullURL} />;
      default:
        return (
          <div>
            <img src={media.fullURL} className="product-image" />
          </div>
        );
    }
  };

  const deleteMedia = (mediaToDelete: MediaModel) => {
    const selectedProduct = itemStore.selectedProduct ?? new ProductItemModel();

    const updatedOptions = selectedProduct.variants?.options.map(
      (option, index) => {
        if (index === itemStore.selectedProduct?.selectedVariantIndex) {
          return new ProductAttributeMappingsInput({
            ...option,
            attributeMappings: option.attributeMappings.map((mapping) => {
              return new ProductAttributeWithOption({
                ...mapping,
                medias: mapping.medias.filter(
                  (media) => media.mediaId !== mediaToDelete.mediaId
                ),
              });
            }),
          });
        }
        return option;
      }
    ) as ProductAttributeMappingsInput[];

    const newProduct = new ProductItemModel({
      ...selectedProduct,
      variants: new ProductAttributeInputModel({
        ...selectedProduct.variants,
        options: updatedOptions,
      }),
    });

    newProduct.editableVariant = ProductVariantEditableModel.init(newProduct);
    itemStore.updateSelectedProduct(newProduct);
  };

  const updateMedia = (
    medias: MediaModel[],
    isSingleMedia: boolean = false
  ) => {
    const selectedProduct = itemStore.selectedProduct ?? new ProductItemModel();

    const updatedOptions = itemStore.selectedProduct?.variants?.options.map(
      (option, index) => {
        if (index === itemStore.selectedProduct?.selectedVariantIndex) {
          return new ProductAttributeMappingsInput({
            ...option,
            attributeMappings: option.attributeMappings.map((mapping) => {
              return new ProductAttributeWithOption({
                ...mapping,
                medias: [
                  ...mapping.medias,
                  ...(isSingleMedia ? [medias[0]] : medias),
                ],
              });
            }),
          });
        }
        return option;
      }
    );

    const newProduct = new ProductItemModel({
      ...selectedProduct,
      variants: new ProductAttributeInputModel({
        ...selectedProduct.variants,
        options: updatedOptions,
      }),
      showLibrary: isSingleMedia ? false : selectedProduct.showLibrary,
    });
    newProduct.editableVariant = ProductVariantEditableModel.init(newProduct);
    itemStore.updateSelectedProduct(newProduct);
  };

  const onUpload = (files: FileList | null, existingMedia: MediaModel[]) => {
    if (files && files.length > 3) {
      ToastService.showWarning(
        "You are only allowed to upload a maximum of 3 files"
      );
      return false;
    }
    const filteredList = Array.from(files || []).filter(
      (file) => file.size / 1048576 <= 5
    );
    if (Array.from(files || []).length > 0 && filteredList.length === 0)
      return ToastService.showError("Please upload file less than 5MB");

    const isMediaDuplicate = (media: MediaModel): boolean => {
      return (
        existingMedia?.some(
          (existingMedia) => existingMedia.mediaId === media.mediaId
        ) || false
      );
    };

    MediaService.uploadMedia({
      AccountId: AuthService.authData.accountId,
      Files: filteredList,
    }).subscribe({
      next: (result) => {
        if (result?.error) {
          ToastService.showError(result.error);
          return;
        }

        const medias =
          result.medias?.map((m) => MediaModel.deserializeAPIModel(m)) || [];
        const nonDuplicateMedias = medias.filter(
          (media) => !isMediaDuplicate(media)
        );

        if (nonDuplicateMedias.length === 0) {
          ToastService.showError("Media is already present.");
        } else {
          updateMedia(nonDuplicateMedias);
          ToastService.showSuccess("Media uploaded successfully");
        }
      },
      error: () => {
        ToastService.showError(
          "Error in uploading media. Please try again later."
        );
      },
    });
  };

  const onClickLibrary = () => {
    itemStore.selectedProduct?.toggleShowLibrary();
    itemStore.updateSelectedProduct(
      new ProductItemModel({ ...itemStore.selectedProduct })
    );
  };

  const onClose = () => {
    itemStore.selectedProduct?.toggleShowLibrary();
    itemStore.updateSelectedProduct(
      new ProductItemModel({ ...itemStore.selectedProduct })
    );
  };

  if (itemStore.selectedProduct.isEditVariant) {
    const selectedVariant =
      itemStore.selectedProduct.variants?.options[
        itemStore.selectedProduct.selectedVariantIndex
      ];
    const medias = selectedVariant.attributeMappings[0].medias;
    const isMediaDuplicate = (media: MediaModel): boolean => {
      return (
        medias?.some(
          (existingMedia) => existingMedia.mediaId === media.mediaId
        ) || false
      );
    };
    return (
      <>
        <MainOuterCard>
          {!itemStore.selectedProduct.editableVariant && (
            <FlexContainer
              spacing={10}
              alignItems="center"
              justifyContent="space-between"
            >
              <CustomLabel
                fontSize="20px"
                fontWeight="600"
                margin="0px 0px 30px"
              >
                {variant.name}
              </CustomLabel>
            </FlexContainer>
          )}

          {/* Add media here  */}
          <FlexContainer justifyContent="space-between" padding="0px 0px 30px">
            <CustomLabel color="#2C3E50" fontSize={"20px"} fontWeight={"600"}>
              Media
            </CustomLabel>
            <div>
              <FlexContainer>
                <div>
                  <IconButton
                    className="transparent-btn"
                    minWidth={"120px"}
                    onClick={onClickLibrary}
                  >
                    <img src={LibraryIcon} />
                    Library
                  </IconButton>
                </div>
                <div>
                  <input
                    style={{ display: "none" }}
                    type="file"
                    id="fileInput"
                    multiple={true}
                    ref={fileUploadBtnRef}
                    accept="video/*,image/*"
                    onChange={(evt) =>
                      onUpload(evt.currentTarget.files, medias)
                    }
                  />
                  <IconButton
                    className="transparent-btn"
                    minWidth={"120px"}
                    onClick={uploadClick}
                  >
                    <img src={UploadIcon} />
                    Upload
                  </IconButton>
                </div>
              </FlexContainer>
            </div>
          </FlexContainer>
          <FlexContainer spacing={20} width="100%">
            <div className="flex-auto-grow">
              {" "}
              <ItemFrame className="full-width">
                <FlexContainer spacing={20} fxWrap="wrap" width="100%">
                  {medias.map((media, mediaIdx) => (
                    <ItemImage
                      className="uploaded-image multiple-media-variant"
                      key={mediaIdx}
                    >
                      {renderMedia(media)}
                      <div className="overlay-product">
                        <FlexContainer
                          alignItems="center"
                          justifyContent="center"
                          className="full-height"
                          spacing={10}
                        >
                          <img
                            onClick={() => deleteMedia(media)}
                            src={DeleteIcon}
                            alt="Delete Icon"
                            className="delete-hovered-icon"
                            // className="plus-hovered"
                          />
                        </FlexContainer>
                      </div>
                    </ItemImage>
                  ))}
                </FlexContainer>
              </ItemFrame>
            </div>
          </FlexContainer>
        </MainOuterCard>
        <AttributeSectionEditable />
        {itemStore.selectedProduct.detailSection.hasVariants &&
          itemStore.selectedProduct.editableVariant && (
            <VariantPriceSectionEditable
              editableVariant={itemStore.selectedProduct.editableVariant}
              onUpdate={(variant) =>
                itemStore.updateSelectedProduct(
                  new ProductItemModel({
                    ...itemStore.selectedProduct,
                    editableVariant: variant,
                  })
                )
              }
            />
          )}
        <VariantAdvancedSectionEditable
          isTrackInventory={itemStore.selectedProduct.isTrackInventory}
          editableVariant={itemStore.selectedProduct.editableVariant}
          onUpdate={(variant) =>
            itemStore.updateSelectedProduct(
              new ProductItemModel({
                ...itemStore.selectedProduct,
                editableVariant: variant,
              })
            )
          }
        />
        {itemStore.selectedProduct.showLibrary && (
          <MediaLibraryPopUp
            mediaStore={mediaStore}
            onUpload={(media: MediaModel) => {
              if (!isMediaDuplicate(media)) {
                updateMedia([media], true);
              } else {
                ToastService.showError("Media is already present.");
              }
            }}
            onClose={() => onClose()}
          />
        )}
      </>
    );
  }

  const selectedVariant =
    itemStore.selectedProduct.variants?.options[
      itemStore.selectedProduct.selectedVariantIndex
    ];
  const medias = selectedVariant.attributeMappings[0].medias;
  return (
    <>
      <FlexContainer
        spacing={10}
        justifyContent="space-between"
        alignItems="center"
      >
        <CustomLabel fontSize="20px" fontWeight="600" margin="0px 0px 30px">
          {variant.name}
        </CustomLabel>
        <SapphireBlueButton
          minWidth="115px"
          padding="7px 10px"
          onClick={() => {
            if (!rolesAndPermissionStore.hasManagePermission("Edit")) return;
            itemStore?.toggleEditVariants(true);
          }}
        >
          Edit Variant
        </SapphireBlueButton>
      </FlexContainer>
      <FlexContainer spacing={20}>
        <ItemFrame>
          <FlexContainer spacing={30} fxWrap="wrap">
            {medias.map((media, mediaIdx) => (
              <ItemImage
                className="uploaded-image multiple-media-variant"
                key={mediaIdx}
              >
                {renderMedia(media)}
              </ItemImage>
            ))}
          </FlexContainer>
        </ItemFrame>
      </FlexContainer>

      {/* <CustomLabel fontSize="20px" fontWeight="600" margin="30px 0px 30px">
              Attributes
          </CustomLabel> */}

      <AttributeSection attributeMappings={variant.attributeMappings} />

      {/* <VariantPriceSection
              variant={itemStore.selectedProduct.variants.options[itemStore.selectedProduct.selectedVariantIndex]}
              attribute={itemStore.selectedProduct.variants}
              standardPricing={itemStore.selectedProduct?.pricing?.termSettings?.standardPricing}
          /> */}

      <ItemAlowSection
        tax={itemStore.selectedProduct.detailSection.tax}
        surcharge={itemStore.selectedProduct.detailSection.surcharge}
        discount={itemStore.selectedProduct.detailSection.discount}
        reviews={itemStore.selectedProduct.detailSection.review}
        returnDays={itemStore.selectedProduct.detailSection.returnDays}
      />
      <ItemInventorySection
        onUpdate={(inventorySettings) =>
          itemStore.updateSelectedProduct(
            new ProductItemModel({
              ...itemStore.selectedProduct,
              inventorySettings: new InventorySettings(inventorySettings),
            })
          )
        }
        inventorySettings={itemStore.selectedProduct.inventorySettings}
        hasVariants={itemStore.selectedProduct.detailSection.hasVariants}
        productId={itemStore.selectedProduct.detailSection.id}
        isTrackInventory={itemStore.selectedProduct.isTrackInventory}
        rolesAndPermissionStore={rolesAndPermissionStore}
      />
    </>
  );
};
export default inject("itemStore")(observer(VariantRightSection));
